import { useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { ChargeDto } from '@tiendanube/common';
import { useAppDispatch } from 'App/store';
import { combineStatus } from 'commons/utils/combineStatus';
import { convertStatusTypeToObject } from 'commons/utils/convertStatusTypeToObject';
import { useGetIsFreePlan } from 'domains/Auth/hooks';
import {
  getChargesToPaySelected,
  setChargesToPaySelected as setChargesToPaySelectedAction,
} from 'domains/Billing/Checkout/CheckoutSlice';
import {
  useChargesToPay,
  usePaymentMethods,
} from 'domains/Billing/Checkout/hooks';
import { useStoreChurnedStatus } from 'domains/Billing/hooks';
import useChargeCreation from './useChargeCreation';

function useWithVirtualCharge(chargesToSelect: ChargeDto[] | undefined) {
  const { isTrial } = useGetIsFreePlan();

  const { status, isStoreDown, hasActivePlanCharge } = useStoreChurnedStatus();

  const planIsListed = useMemo(
    () =>
      chargesToSelect?.some(({ conceptCode }) => conceptCode === 'plan-cost'),
    [chargesToSelect],
  );

  const withVirtualCharge = useMemo(
    () => !planIsListed && !hasActivePlanCharge && (isTrial || isStoreDown),
    [planIsListed, hasActivePlanCharge, isTrial, isStoreDown],
  );

  return {
    withVirtualCharge,
    status,
  };
}

export default function useSelectCharges(init = false) {
  const { status: chargeCreationStatus, onContinue: onContinueChargeCreation } =
    useChargeCreation();

  const {
    groupedCharges,
    chargesToSelect,
    status: chargesStatus,
    refreshChargesToPay,
    currentPlanCharge,
  } = useChargesToPay();

  const { withVirtualCharge, status: virtualChargeStatus } =
    useWithVirtualCharge(chargesToSelect);

  const {
    status: methodsStatus,
    firstRecurrencyOptionAsCharge,
    refreshPaymentMethods,
  } = usePaymentMethods(withVirtualCharge);

  const chargeGroups = useMemo(() => {
    if (!groupedCharges) return undefined;
    const groups = groupedCharges.filter((group) =>
      group.every((charge) =>
        chargesToSelect?.map(({ id }) => id).includes(charge.id),
      ),
    );
    return withVirtualCharge
      ? firstRecurrencyOptionAsCharge && [
          [firstRecurrencyOptionAsCharge],
          ...groups,
        ]
      : groups;
  }, [
    groupedCharges,
    firstRecurrencyOptionAsCharge,
    withVirtualCharge,
    chargesToSelect,
  ]);

  const { status } = combineStatus(
    virtualChargeStatus,
    withVirtualCharge ? methodsStatus : undefined,
    chargesStatus,
  );

  const dispatch = useAppDispatch();
  const setSelectedCharges = useCallback(
    (charges: ChargeDto[]) => dispatch(setChargesToPaySelectedAction(charges)),
    [dispatch],
  );
  const selectedCharges = useSelector(getChargesToPaySelected);

  useEffect(() => {
    if (!init || !chargeGroups) return;
    setSelectedCharges([
      ...chargeGroups
        .reduce((acc, group) => acc.concat(group), [])
        .filter((charge) => charge.status !== 'IN_PROCESS'),
    ]);
  }, [chargeGroups, setSelectedCharges, init]);

  const onPay = useCallback(() => {
    onContinueChargeCreation(selectedCharges);
  }, [onContinueChargeCreation, selectedCharges]);

  const selectCharge = useCallback(
    (checked: boolean, charges: ChargeDto[]) => {
      if (checked) {
        setSelectedCharges([...selectedCharges, ...charges]);
      } else {
        setSelectedCharges(
          selectedCharges.filter(
            (ch) => !charges.some(({ id }) => id === ch.id),
          ),
        );
      }
    },
    [setSelectedCharges, selectedCharges],
  );

  const retryLoad = useCallback(() => {
    refreshChargesToPay();
    withVirtualCharge && refreshPaymentMethods();
  }, [refreshChargesToPay, refreshPaymentMethods, withVirtualCharge]);

  const planChargeToUpdate = useMemo(() => {
    if (withVirtualCharge) return firstRecurrencyOptionAsCharge;
    return currentPlanCharge;
  }, [currentPlanCharge, firstRecurrencyOptionAsCharge, withVirtualCharge]);

  return {
    status,
    retryLoad,
    setSelectedCharges,
    chargesToSelect,
    chargeGroups,
    onPay,
    payOrderCreationStatus: chargeCreationStatus,
    isCreatingPayOrder: chargeCreationStatus === 'loading',
    selectedCharges,
    selectCharge,
    ...convertStatusTypeToObject(status),
    virtualCharge: firstRecurrencyOptionAsCharge,
    planChargeToUpdate,
  };
}
